import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { Badge } from '../../../../../components/ui/badge';
import { Card, CardContent } from '../../../../../components/ui/card';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../../components/ui/table';
import { Checkbox } from '../../../../../components/ui/checkbox';
import { ArrowLeft, Plus } from 'lucide-react';
import type { DashboardProject } from '../../../types/project';

export interface CoatingQCModalProps {
  open: boolean;
  onClose: () => void;
  project?: DashboardProject;
  /** Selected spool from spool tracker (form default). */
  spoolName?: string;
}

const DFT_ROWS = [
  { loc: "1A — 12 o'clock", defaultVal: '482', ok: true },
  { loc: "1B — 3 o'clock", defaultVal: '455', ok: true },
  { loc: "1C — 6 o'clock", defaultVal: '310', ok: false },
  { loc: "1D — 9 o'clock", defaultVal: '498', ok: true },
];

function CheckLine({ label, defaultChecked }: { label: string; defaultChecked?: boolean }) {
  const [checked, setChecked] = useState(!!defaultChecked);
  return (
    <label className="flex cursor-pointer items-center gap-3 rounded-md border border-gray-200 bg-white px-3 py-2.5 text-sm hover:bg-gray-50">
      <Checkbox checked={checked} onCheckedChange={(v) => setChecked(v === true)} />
      <span className="text-gray-800">{label}</span>
    </label>
  );
}

export const CoatingQCModal: React.FC<CoatingQCModalProps> = ({ open, onClose, project, spoolName }) => {
  const spoolLabel = spoolName?.trim() || 'SPOOL-3';
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-screen h-screen max-w-none m-0 gap-0 border-0 bg-white p-0 shadow-none rounded-none [&>button]:hidden">
        <div className="flex h-screen flex-col overflow-hidden">
          <DialogHeader className="flex shrink-0 flex-row items-start justify-between gap-4 border-b bg-white px-4 py-4 sm:px-6">
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <Button type="button" variant="outline" size="icon" className="shrink-0" onClick={onClose} aria-label="Back">
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </Button>
              <div className="min-w-0">
                <DialogTitle className="text-left text-lg font-bold text-gray-900">Coating QC</DialogTitle>
                {project?.project?.projectName && (
                  <p className="mt-1 truncate text-sm text-gray-500">{project.project.projectName}</p>
                )}
                {spoolName?.trim() && (
                  <p className="mt-0.5 truncate text-sm font-medium text-gray-700">Spool: {spoolName.trim()}</p>
                )}
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <Button type="button" size="sm" className="gap-1.5 bg-[#1E6353] text-white hover:bg-[#175544]">
                <Plus className="h-4 w-4" />
                Log Coating Inspection
              </Button>
              {/* <Button type="button" variant="outline" size="sm" onClick={onClose}>
                Close
              </Button> */}
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6">
            <div className="mx-auto max-w-full space-y-6">
              {/* <div className="rounded-lg border border-amber-200 bg-amber-50/90 px-4 py-3 text-sm text-amber-950">
                <p className="text-xs font-bold uppercase tracking-wide text-amber-800">Product Manager Note</p>
                <p className="mt-2 leading-relaxed">
                  <strong>Replaces</strong>: Paper coating inspection reports, manual DFT logs, holiday test records.
                  <br />
                  <strong>Key users</strong>: Coating Inspector, QC Inspector.
                  <br />
                  <strong>Standard</strong>: NORSOK M-501 System 7C, EP-GIS 52-409. Epoxy over FBE interface test is mandatory.
                </p>
              </div> */}

              <Card className="border border-gray-200 shadow-sm">
                <div className="flex flex-col gap-2 border-b bg-gray-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                  <h3 className="text-base font-semibold text-gray-900">Coating QC Record</h3>
                  <Badge variant="outline" className="w-fit border-violet-200 bg-violet-50 text-violet-900">
                    Coating Inspector
                  </Badge>
                </div>
                <CardContent className="space-y-5 p-4 sm:p-6">
                  <div className="grid gap-3 sm:grid-cols-3">
                    {/* <div className="space-y-1.5">
                      <Label className="text-xs text-gray-700">
                        Spool <span className="text-red-500">*</span>
                      </Label>
                      <Select defaultValue="current">
                        <SelectTrigger className="h-9 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="current">{spoolLabel}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div> */}
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-700">
                        Area Type <span className="text-red-500">*</span>
                      </Label>
                      <Select defaultValue="bend">
                        <SelectTrigger className="h-9 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="bend">Bend</SelectItem>
                          <SelectItem value="tie">Weld FBE Tie-In</SelectItem>
                          <SelectItem value="flange">Flange OD</SelectItem>
                          <SelectItem value="cutback">Bare Cutback</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-gray-700">Area Description</Label>
                      <Input
                        className="h-9 text-sm"
                        placeholder="e.g. Bend B1 — 12 o'clock to 6 o'clock"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-gray-700">Inspection Date</Label>
                      <Input className="h-9 text-sm" type="date" defaultValue="2026-04-06" />
                    </div>
                  </div>

                  <div>
                    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-800">
                      Environmental Conditions
                    </h4>
                    <div className="grid gap-3 sm:grid-cols-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-gray-700">Ambient Temp (°C)</Label>
                        <Input className="h-9 text-sm" placeholder="e.g. 32.5" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-gray-700">Humidity (%)</Label>
                        <Input className="h-9 text-sm" placeholder="e.g. 78" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-gray-700">Dew Point (°C)</Label>
                        <Input className="h-9 text-sm" placeholder="e.g. 28.1" />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-800">DFT Readings</h4>
                    {/* <div className="mb-3 rounded-md border border-blue-100 bg-blue-50/60 px-3 py-2 text-[11px] text-blue-950">
                      <span className="font-semibold text-blue-800">Smart Feature</span> — DFT readings can be entered
                      point-by-point. The system can auto-calculate min/max and flag readings outside the 350–700 µm
                      specification window.
                    </div> */}
                    <div className="overflow-hidden rounded-md border border-gray-200">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50 hover:bg-gray-50">
                            <TableHead className="text-xs font-semibold">Location</TableHead>
                            <TableHead className="text-xs font-semibold">DFT (µm)</TableHead>
                            <TableHead className="text-xs font-semibold">Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {DFT_ROWS.map((row) => (
                            <TableRow key={row.loc}>
                              <TableCell className="text-sm text-gray-800">{row.loc}</TableCell>
                              <TableCell>
                                <Input className="h-9 w-28 text-sm" defaultValue={row.defaultVal} />
                              </TableCell>
                              <TableCell>
                                {row.ok ? (
                                  <Badge className="border-transparent bg-emerald-50 text-emerald-800">OK</Badge>
                                ) : (
                                  <Badge className="border-transparent bg-red-50 text-red-800">Below Min</Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <CheckLine label="Surface preparation to Sa 2.5 — acceptable" defaultChecked />
                    <CheckLine label="Holiday test — no holidays detected" defaultChecked />
                    <CheckLine label="Interface test (epoxy over FBE) — passed" />
                  </div>

                  <div className="flex flex-wrap justify-end gap-2 border-t pt-4">
                    <Button type="button" variant="outline">
                      Save Draft
                    </Button>
                    <Button type="button" className="bg-red-600 text-white hover:bg-red-700">
                      Raise NCR
                    </Button>
                    <Button type="button" className="bg-emerald-600 text-white hover:bg-emerald-700">
                      Submit Coating Record
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
